import type { ReactNode } from "react";
import type { TimeWindow } from "../types";
import { TIME_WINDOWS } from "../types";
import { cls, fmtRelative } from "../format";

interface Tab {
  id: string;
  label: string;
}

interface LayoutProps {
  tabs: Tab[];
  activeTab: string;
  onTabChange: (id: string) => void;
  window: TimeWindow;
  onWindowChange: (w: TimeWindow) => void;
  lastUpdated: Date | null;
  loading: boolean;
  onRefresh: () => void;
  onLogout?: () => void;
  showWindowSelector?: boolean;
  children: ReactNode;
}

export const Layout = ({
  tabs,
  activeTab,
  onTabChange,
  window,
  onWindowChange,
  lastUpdated,
  loading,
  onRefresh,
  onLogout,
  showWindowSelector = true,
  children,
}: LayoutProps) => {
  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">
          <span className="brand-mark" />
          <div>
            <div className="brand-title">CENTRAL API</div>
            <div className="brand-sub">live ops dashboard</div>
          </div>
        </div>

        <nav className="tabs" aria-label="Sections">
          {tabs.map((t) => (
            <button
              key={t.id}
              className={cls("tab", activeTab === t.id && "tab-on")}
              onClick={() => onTabChange(t.id)}
              type="button"
            >
              {t.label}
            </button>
          ))}
        </nav>

        <div className="topbar-right">
          {showWindowSelector && (
            <div className="window-selector" role="group" aria-label="Time window">
              {TIME_WINDOWS.map((w) => (
                <button
                  key={w}
                  className={cls("chip", window === w && "chip-on")}
                  onClick={() => onWindowChange(w)}
                  type="button"
                >
                  {w}
                </button>
              ))}
            </div>
          )}
          <button
            className={cls("refresh", loading && "refresh-spinning")}
            onClick={onRefresh}
            title="Refresh now"
            type="button"
          >
            ↻
          </button>
          <span className="last-updated" title={lastUpdated?.toISOString() || ""}>
            {loading
              ? "loading…"
              : lastUpdated
                ? `updated ${fmtRelative(lastUpdated)}`
                : "—"}
          </span>
          {onLogout && (
            <button 
              className="tab" 
              onClick={onLogout} 
              style={{ marginLeft: '12px', border: '1px solid var(--red-soft)', color: 'var(--red)', fontSize: '10px' }}
              type="button"
            >
              LOGOUT
            </button>
          )}
        </div>
      </header>

      <main className="content">{children}</main>

      <footer className="footer">
        <span>central-api · dashboard</span>
        <span className="dim">auto-refreshes every 15s</span>
      </footer>
    </div>
  );
};
